import { useState, useCallback, useRef, useEffect } from 'react';
import { VOICE_WS_URL, type VoiceLanguage } from '@/api/config';
import { getDeviceId, getUUID, getAuthToken } from '@/shared/utils';
import type {
  VoiceAudioConfig,
  VoiceMessage,
  VoiceText,
  VoiceConfidence,
  VoiceAutoSubmit,
} from '@/api/contracts';

/**
 * Current state of the voice recording process.
 */
export type RecordingState = 'idle' | 'connecting' | 'recording' | 'error';

/**
 * Result interface returned by the useVoiceRecording hook.
 */
export interface VoiceRecordingResult {
  /** Current recording state */
  state: RecordingState;
  /** Partial transcription (while speaking) */
  partialText: VoiceText;
  /** Final transcription text */
  finalText: VoiceText;
  /** Error message if any */
  error: string | null;
  /** Confidence score of final transcription (0-1) */
  confidence: VoiceConfidence;
  /** Whether auto-submit is suggested by the backend */
  autoSubmit: VoiceAutoSubmit;
  /** Start recording with optional language */
  startRecording: (language?: VoiceLanguage) => Promise<void>;
  /** Stop recording */
  stopRecording: () => void;
  /** Clear transcription and error state */
  reset: () => void;
}

// ============================================================================
// Audio Processing Utilities
// ============================================================================

/**
 * Convert Float32Array audio samples to Int16Array (PCM 16-bit)
 * This is the format expected by Azure Speech Services
 */
function float32ToInt16(float32Array: Float32Array): Int16Array {
  const int16Array = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    // Clamp to [-1, 1] range (using 0 as fallback for safety)
    const rawSample = float32Array[i] ?? 0;
    const sample = Math.max(-1, Math.min(1, rawSample));
    // Convert to 16-bit signed integer
    int16Array[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return int16Array;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useVoiceRecording(): VoiceRecordingResult {
  const [state, setState] = useState<RecordingState>('idle');
  const [partialText, setPartialText] = useState<VoiceText>('');
  const [finalText, setFinalText] = useState<VoiceText>('');
  const [error, setError] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<VoiceConfidence>(null);
  const [autoSubmit, setAutoSubmit] = useState<VoiceAutoSubmit>(false);

  // Refs for cleanup
  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss error after 5 seconds
  const setErrorWithAutoDismiss = useCallback((errorMessage: string) => {
    // Clear any existing timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    setError(errorMessage);
    // Auto-dismiss after 5 seconds
    errorTimeoutRef.current = setTimeout(() => {
      setError(null);
      setState('idle');
    }, 5000);
  }, []);

  /**
   * Cleanup all audio and WebSocket resources
   */
  const cleanup = useCallback(() => {
    // Disconnect audio processor
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    // Stop all media tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {
        // Ignore errors during cleanup
      });
      audioContextRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send('stop');
      }
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  /**
   * Initialize audio recording after WebSocket connection
   */
  const initializeAudio = useCallback(
    async (audioConfig: VoiceAudioConfig) => {
      try {
        // Check if mediaDevices API is available (requires HTTPS)
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Microphone access requires HTTPS. Please use a secure connection.');
        }

        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: audioConfig.sampleRate,
            channelCount: audioConfig.channels,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        mediaStreamRef.current = stream;

        // Create audio context with matching sample rate
        const audioContext = new AudioContext({ sampleRate: audioConfig.sampleRate });
        audioContextRef.current = audioContext;

        // Create audio source from microphone
        const source = audioContext.createMediaStreamSource(stream);

        // Create script processor for audio processing
        // Buffer size matches what the backend expects
        const processor = audioContext.createScriptProcessor(audioConfig.bufferSize, 1, 1);
        processorRef.current = processor;

        // Process audio chunks and send to WebSocket
        processor.onaudioprocess = (event) => {
          const ws = wsRef.current;
          if (ws && ws.readyState === WebSocket.OPEN) {
            const float32Data = event.inputBuffer.getChannelData(0);
            const int16Data = float32ToInt16(float32Data);
            ws.send(int16Data.buffer);
          }
        };

        // Connect the audio pipeline
        source.connect(processor);
        processor.connect(audioContext.destination);

        setState('recording');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to access microphone';
        setError(message);
        setState('error');
        cleanup();
      }
    },
    [cleanup],
  );

  /**
   * Handle incoming WebSocket messages
   */
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string) as VoiceMessage;

        switch (data.type) {
          case 'init_result':
            if (data.status === 'READY') {
              void initializeAudio(data.audioConfig);
            } else {
              setError(data.message ?? 'Failed to initialize voice session');
              setState('error');
              cleanup();
            }
            break;

          case 'transcription':
            if (data.final) {
              setFinalText(data.text);
              setPartialText('');
              setConfidence(data.confidence ?? null);
              setAutoSubmit(data.autoSubmit ?? false);
            } else {
              setPartialText(data.text);
            }
            break;

          case 'error':
            setError(data.message);
            if (data.fatal) {
              setState('error');
              cleanup();
            }
            break;

          case 'session_end':
            setState('idle');
            cleanup();
            break;
        }
      } catch {
        console.error('Failed to parse voice message');
      }
    },
    [cleanup, initializeAudio],
  );

  /**
   * Start voice recording
   */
  const startRecording = useCallback(
    async (language: VoiceLanguage = 'en-IN') => {
      // Reset state
      setError(null);
      setPartialText('');
      setFinalText('');
      setConfidence(null);
      setAutoSubmit(false);
      setState('connecting');

      try {
        // Build WebSocket URL with language and auth parameters
        // WebSocket API doesn't support custom headers, so pass auth via query params
        const wsParams = new URLSearchParams({ language });
        const authToken = getAuthToken();
        if (authToken) wsParams.set('mmtAuth', authToken);
        const uuid = getUUID();
        if (uuid) wsParams.set('uuid', uuid);
        const deviceId = getDeviceId();
        if (deviceId) wsParams.set('deviceId', deviceId);

        const wsUrl = `${VOICE_WS_URL}?${wsParams.toString()}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          // Voice WebSocket connected successfully
        };

        ws.onmessage = handleMessage;

        ws.onerror = () => {
          setErrorWithAutoDismiss('WebSocket connection error');
          setState('error');
          cleanup();
        };

        ws.onclose = (event) => {
          if (state === 'recording' || state === 'connecting') {
            // Unexpected close
            if (event.code !== 1000) {
              setErrorWithAutoDismiss('Connection closed unexpectedly');
              setState('error');
            } else {
              setState('idle');
            }
          }
          cleanup();
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to connect';
        setErrorWithAutoDismiss(message);
        setState('error');
        cleanup();
      }
    },
    [cleanup, handleMessage, state, setErrorWithAutoDismiss],
  );

  /**
   * Stop voice recording
   */
  const stopRecording = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send('stop');
    }
    setState('idle');
    cleanup();
  }, [cleanup]);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    // Clear error timeout when manually resetting
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    setError(null);
    setPartialText('');
    setFinalText('');
    setConfidence(null);
    setAutoSubmit(false);
    setState('idle');
    cleanup();
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear error timeout on unmount
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
      cleanup();
    };
  }, [cleanup]);

  return {
    state,
    partialText,
    finalText,
    error,
    confidence,
    autoSubmit,
    startRecording,
    stopRecording,
    reset,
  };
}
